import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
// uppy
import Uppy from '@uppy/core';
import AwsS3 from '@uppy/aws-s3';
import { DragDrop, StatusBar } from '@uppy/react';
// @material-ui/core components
import withStyles from '@material-ui/core/styles/withStyles';
// core components
import Button from 'components/UI/CustomButtons/Button';
// our components
import AvatarStyle from 'assets/jss/Avatar';
import defaultAvatar from 'assets/img/placeholder.jpg';
import agIcon from 'assets/img/AgencyIco.svg';
import userAgIcon from 'assets/img/UserAgencyIco.svg';
import prepareImageUpload from 'helpers/uppyHelper';
import { userKinds } from 'config';

/**
 * @class
 * @extends React.Component
 */
class Avatar extends React.Component {
  constructor(props) {
    super(props);
    const { imageType } = props;
    this.state = {
      file: null,
    };
    this.uppy = new Uppy({
      meta: { type: 'avatar' },
      allowedFileTypes: ['image/*'],
      allowMultipleUploads: false,
      autoProceed: false,
    });
    this.uppy.use(AwsS3, {
      getUploadParameters: () => prepareImageUpload(imageType),
    });
  }

  /**
   * @description
   * React lifecycle method. Is called when component is mounted.
   */
  componentDidMount() {
    const { imageChange, uploadComplete, onRef } = this.props;
    // add uppy callbacks
    this.uppy.on('file-added', img => {
      // update state to show image when added
      const reader = new FileReader();
      reader.readAsDataURL(img.data);
      reader.onloadend = () => {
        this.setState({
          file: img,
        });
        imageChange(reader.result);
      };
    });

    this.uppy.on('complete', result => {
      // update and show image from server
      if (result.successful.length === 1) {
        uploadComplete(result.successful[0].meta.key);
      }
    });

    // set ref callback
    onRef(this);
  }

  /**
   * @description
   * React lifecycle method. Is called when component is mounted.
   */
  componentWillUnmount() {
    const { onRef } = this.props;
    this.uppy.close();
    onRef(undefined);
  }

  /**
   * @description
   * UPload File method. This method will be called from parent via ref.
   */
  uploadFile = () => {
    this.uppy.upload();
  };

  /**
   * @description
   * Handle remove image.
   */
  handleRemove = () => {
    const { file } = this.state;
    const { imageChange } = this.props;
    if (this.uppy.getFiles().length > 0) {
      this.uppy.removeFile(file.id);
      this.setState({ file: null });
    }
    imageChange(null);
  };

  /**
   * @description
   * Render Uppy, DragDrop components
   */
  render() {
    const { imageUrl, avatar, userType, size, classes, t } = this.props;
    return (
      <React.Fragment>
        <div className={classes.container}>
          <div
            className={`${classes.imgContainer} ${
              avatar ? classes.avatarImg : ''
            }`}
            style={size && { width: size, height: size }}
          >
            <img
              className={classes.mainImg}
              src={imageUrl || defaultAvatar}
              alt="avatar"
            />
            {userType === userKinds.AGENCY && (
              <img className={classes.agLogo} src={agIcon} alt="avatar" />
            )}
            {userType === userKinds.USER_AGENCY && (
              <img className={classes.agLogo} src={userAgIcon} alt="avatar" />
            )}
          </div>
          {!imageUrl && (
            <div
              className={`${classes.uppyCompStyles} ${
                avatar ? classes.isAvatar : classes.isImage
              }`}
              style={{
                width: size + 20,
                height: size + 20,
              }}
            >
              <DragDrop
                uppy={this.uppy}
                locale={{
                  strings: {
                    dropHereOr: `${t('common.uppy.drop')} %{browse}`,
                    browse: t('common.uppy.browse'),
                  },
                }}
              />
            </div>
          )}
          <StatusBar
            uppy={this.uppy}
            hideUploadButton
            hideAfterFinish
            showProgressDetails
          />

          {imageUrl && (
            <Button
              id="deleteAvatar"
              simple
              color="danger"
              onClick={this.handleRemove}
            >
              {t('common.erase')}
            </Button>
          )}
        </div>
      </React.Fragment>
    );
  }
}

/**
 * Avatar PropTypes
 * @param imageUrl - image gcs_key
 * @param t - prop from HOC with string translations
 * @callback imageChange - callback called when the current image changes
 * @callback uploadComplete - callback called when the image is uploaded
 * successfully
 * @callback onRef - function for set element as ref
 */
Avatar.propTypes = {
  imageUrl: PropTypes.string,
  imageChange: PropTypes.func.isRequired,
  uploadComplete: PropTypes.func.isRequired,
  onRef: PropTypes.func.isRequired,
  avatar: PropTypes.bool,
  userType: PropTypes.string,
  size: PropTypes.number,
  classes: PropTypes.instanceOf(Object).isRequired,
  imageType: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
};

Avatar.defaultProps = {
  imageUrl: '',
  avatar: false,
  userType: userKinds.USER,
  size: 100,
};

export default withTranslation()(withStyles(AvatarStyle)(Avatar));
